import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  DocumentDuplicateIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useUser } from "src/hooks/useUser";
import {
  createReportTemplate,
  deleteReportTemplate,
  updateReportTemplateOrder,
} from "src/services/reportTemplate";
import SortableItem from "../Sortable/SortableItem";
import Button from "../Inputs/Button";
import { useEffect, useRef, useState } from "react";
import ModalInput from "../Inputs/ModalInput";
import Input from "../Inputs/Input";
import Confirm from "../Confirm";

const ReportTemplateList = ({
  setEditing,
}: {
  setEditing: (id: string) => void;
}) => {
  const { user, updateUser } = useUser();
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editingMode, setEditingMode] = useState<"new" | "copy">("new");
  const [deleting, setDeleting] = useState<string | null>(null);
  const [copyTemplate, setCopyTemplate] = useState<any>(null);
  const [nameOpen, setNameOpen] = useState<boolean>(false);
  const nameRef = useRef(null as any);
  if (!user) return null;
  useEffect(() => {
    console.log("E", nameRef, nameRef.current, nameOpen);
    if (nameRef.current && nameOpen) {
      nameRef.current.focus();
    }
  }, [nameRef.current, nameOpen]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!user) return;

    if (over && active.id !== over.id) {
      const oldIndex = user?.reportTemplates.findIndex(
        (page) => page.id === active.id,
      );
      const newIndex = user?.reportTemplates.findIndex(
        (page) => page.id === over.id,
      );

      const newSettings = arrayMove(user?.reportTemplates, oldIndex, newIndex);
      const order = newSettings.map((item, i) => [item.id, i + 1]);
      console.log(order);
      updateReportTemplateOrder(order as any)
        .then(() => {
          console.log("Order updated successfully");
        })
        .catch((error) => {
          console.error("Error updating order:", error);
        });

      updateUser({
        reportTemplates: newSettings,
      });
    }
  }

  const save = async () => {
    if (editingMode == "copy") {
      const newTemplate = await createReportTemplate({
        name: editingName || "",
        settings: copyTemplate.settings,
      }).then((res) => res.json());
      updateUser({
        ...user,
        reportTemplates: [...user.reportTemplates, newTemplate],
      });
      setEditing(newTemplate.id);
    } else {
      const newTemplate = await createReportTemplate({
        name: editingName || "",
        settings: [],
      }).then((res) => res.json());
      updateUser({
        ...user,
        reportTemplates: [...user.reportTemplates, newTemplate],
      });
      setEditing(newTemplate.id);
    }
  };

  return (
    <div className="max-w-2xl m-auto pt-20">
      <ModalInput
        isOpen={nameOpen}
        onClose={() => setNameOpen(false)}
        onConfirm={save}
      >
        {nameOpen && (
          <div className="py-3">
            <div className="text-lg font-semibold mb-2">New Template Name:</div>
            <Input
              label=""
              autoFocus
              value={editingName}
              ref={(element: any) => element?.focus()}
              setValue={(name) => setEditingName(name)}
              onKeyDown={(e: any) => {
                if (e.key === "Enter") {
                  save();
                }
              }}
              size="full"
              vertical
            />
          </div>
        )}
      </ModalInput>
      <div className="flex flex-col gap-2 my-5 w-full">
        <div className="flex justify-between">
          <h1 className="text-2xl font-semibold mb-4">Report Templates</h1>
          <div className="w-56">
            <Button
              type="primary"
              onClick={() => {
                setNameOpen(true);
                setEditingName("");
                setEditingMode("new");
              }}
            >
              {" "}
              <div className="flex items-center gap-2 justify-center">
                <PlusIcon className="w-5 h-5 " />
                Create new template
              </div>
            </Button>
          </div>
        </div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={user?.reportTemplates.map((page) => page.id) || []}
            strategy={verticalListSortingStrategy}
          >
            {user?.reportTemplates.map((template) => (
              <SortableItem key={template.id} id={template.id} page={template}>
                <div className="py-2 px-5 w-full h-12 border flex justify-between bg-gray-50 items-center ">
                  <div className="flex gap0">
                    <div className="flex justify-start cursor-pointer mr-2">
                      <EllipsisVerticalIcon className="text-slate-800 w-5 mr-[-15px]" />
                      <EllipsisVerticalIcon className="text-slate-800 w-5 mr-[-15px]" />
                      <EllipsisVerticalIcon className="text-slate-800 w-5 " />
                    </div>
                    {template.name}
                  </div>
                  <div className="flex gap-5">
                    <PencilIcon
                      className="w-6 h-6 cursor-pointer"
                      onClick={() => {
                        setEditing(template.id);
                      }}
                    />
                    <DocumentDuplicateIcon
                      className="w-6 h-6 cursor-pointer"
                      onClick={() => {
                        setEditingName(`${template.name} - copy`);
                        setCopyTemplate(template);
                        setEditingMode("copy");
                        setNameOpen(true);
                      }}
                    />
                    <TrashIcon
                      className="w-6 h-6 text-red-500 cursor-pointer"
                      onClick={() => {
                        setDeleting(template.id);
                      }}
                    />
                  </div>
                </div>
              </SortableItem>
            ))}
          </SortableContext>
        </DndContext>
        <Confirm
          isOpen={!!deleting}
          onClose={() => setDeleting(null)}
          onConfirm={async () => {
            if (deleting) {
              deleteReportTemplate(deleting);
              updateUser({
                ...user,
                reportTemplates: user.reportTemplates.filter(
                  (t) => t.id !== deleting,
                ),
              });
            }
            setDeleting(null);
          }}
        >
          <div className="p-4">
            <h2 className="text-lg font-semibold mb-2">
              Are you sure you want to delete this template?
            </h2>
          </div>
        </Confirm>
      </div>
    </div>
  );
};

export default ReportTemplateList;
